import React, { useRef, useState, useEffect } from "react";
import { useLocation } from "react-router-dom";
import {
  GoogleMap,
  Marker,
  InfoWindow,
  useLoadScript,
} from "@react-google-maps/api";

import { useTheme } from "../../contexts/ThemeContext";
import Footer from "./Footer";
import { useEntryTracking } from "../../contexts/EntryContext";

import { CITIES_DATA } from "../../data/citiesData";
import ContactModal from "./ContactModal";

const API_KEY = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;

const containerStyle = { width: "100%", height: "100%" };
const isMobile = window.innerWidth <= 768;
const initialCenterMobile = { lat: -28.244760, lng: -48.238214 };
const initialCenterWeb = { lat: -25.244760, lng: -49.238214 };
const initialCenter = isMobile ? initialCenterMobile : initialCenterWeb;
const INITIAL_LAT_OFFSET = isMobile ? 2.3 : -0.12;
const mapCenterOffset = {
  lat: initialCenter.lat + INITIAL_LAT_OFFSET,
  lng: initialCenter.lng,
};

// transforma objeto em array
const citiesArray = Object.values(CITIES_DATA);

const MapContent = () => {
  const { theme } = useTheme();
  const { entryCity } = useEntryTracking();
  const location = useLocation();

  const [selectedCity, setSelectedCity] = useState(null);
  const [mapCenter, setMapCenter] = useState(mapCenterOffset);
  const mapRef = useRef(null);
  const CityActive = location.pathname.includes("/conversion/") || location.pathname.includes("/city/");
  const [open, setOpen] = useState(false);

  // Filtra cidades de acordo com o contexto
  const CitiesFilter = citiesArray.filter((c) => {
    const ctx = theme?.context?.toLowerCase() || "";
    if (ctx === "") return true;
    if (ctx.includes("respirat")) return c.cpap === true;
    if (ctx.includes("home")) return c.home === true;
    if (ctx.includes("audit")) return c.aud === true;
    return false;
  });

  useEffect(() => {
    if (entryCity) {
      const cityFromFilter = CitiesFilter.find((c) => c.id === entryCity.id);

      if (cityFromFilter) {
        const target = {
          lat: Number(cityFromFilter.lat),
          lng: Number(cityFromFilter.lng),
        };

        setSelectedCity(cityFromFilter);
        setMapCenter(target);

        if (mapRef.current) {
          mapRef.current.panTo(target);
          mapRef.current.setZoom(11);
        }

        return;
      }
    }

    // Se não houver cidade no entry, não seleciona nenhuma
    setSelectedCity(null);
    setMapCenter(mapCenterOffset);
  }, [entryCity, theme?.context]);

  const handleMapLoad = (map) => {
    mapRef.current = map;
  };

  const handleCityClick = (city, newZoom = 11) => {
    if (!mapRef.current) return;

    const target = {
      lat: Number(city.lat),
      lng: Number(city.lng),
    };

    setSelectedCity(city);
    setMapCenter(target);
    mapRef.current.panTo(target);
    mapRef.current.setZoom(newZoom);
  };

  const handleCloseSelectedCity = () => {
    setSelectedCity(null);
    setMapCenter(mapCenterOffset);

    if (!mapRef.current) return;
    mapRef.current.panTo(mapCenterOffset);
    mapRef.current.setZoom(7);
  };

  const citiesCount = CitiesFilter.length;
  const duration =
    citiesCount <= 1 ? 0 : citiesCount <= 3 ? 45 : 25;

  const citiesToRender =
    citiesCount > 1 ? [...CitiesFilter, ...CitiesFilter] : CitiesFilter;

  const contactCity = selectedCity || CitiesFilter[0] || null;

  const getCustomPinIcon = () => {
    const svg = `
    <svg width="64" height="84" viewBox="0 0 64 84" fill="none" xmlns="http://www.w3.org/2000/svg">
      <!-- Pin -->
      <path
        d="M32 82C32 82 56 52 56 32C56 18.7452 45.2548 8 32 8C18.7452 8 8 18.7452 8 32C8 52 32 82 32 82Z"
        fill="#00394f"
        stroke="white"
        stroke-width="3"
      />

      <!-- Círculo interno -->
      <circle cx="32" cy="32" r="20" fill="white" />

      <!-- Logo -->
      <g transform="translate(14,13) scale(0.14)">
        <path d="M0 0 C1.98 0 3.96 0 6 0 C12.89857705 16.17309907 19.4967025 32.46677238 26.09454346 48.76403809 C27.63354371 52.56449209 29.1742078 56.36427135 30.71484375 60.1640625 C31.02568909 60.93076263 31.33653442 61.69746277 31.65679932 62.48739624 C32.28781371 64.04378569 32.91883587 65.60017199 33.54986572 67.15655518 C34.81838993 70.2852882 36.08685127 73.41404671 37.35528564 76.54281616 C37.98413418 78.09395018 38.61299966 79.64507733 39.24188232 81.19619751 C40.74037702 84.89231114 42.23853912 88.58855876 43.73583984 92.28515625 C50.01700032 107.79160842 56.33690053 123.28052031 62.74707031 138.73413086 C66.20411009 147.06860285 69.63668807 155.41264355 73.03979492 163.76928711 C74.55813276 167.49444131 76.08221627 171.21536222 77.6640625 174.9140625 C78.35628906 176.53441406 78.35628906 176.53441406 79.0625 178.1875 C79.44535156 179.06792969 79.82820312 179.94835937 80.22265625 180.85546875 C81 183 81 183 81 186 C67.14 186 53.28 186 39 186 C33.48945616 172.77469478 33.48945616 172.77469478 31.3125 166.3125 C28.82300576 159.06530566 26.01003639 151.97289785 23.125 144.875 C18.68385376 133.91739775 14.7030233 122.84935222 10.90380859 111.65527344 C9.47939514 107.47052987 8.02504075 103.29654479 6.5625 99.125 C6.31532227 98.41085938 6.06814453 97.69671875 5.81347656 96.9609375 C4.85077482 93.72715746 4.85077482 93.72715746 3 91 C2.72597351 91.78235771 2.72597351 91.78235771 2.44641113 92.58052063 C0.50589126 98.11699031 -1.44306823 103.65044077 -3.39648438 109.18237305 C-4.11991166 111.23437773 -4.84133801 113.28708917 -5.56054688 115.34057617 C-9.50243662 126.59092769 -13.55542278 137.73780563 -18.30761719 148.67500305 C-21.00280561 154.87994204 -23.28616637 161.18474486 -25.48046875 167.58203125 C-27.59864912 173.74054539 -29.80957492 179.86680976 -32 186 C-68.3 186 -104.6 186 -142 186 C-142 126.6 -142 67.2 -142 6 C-48.91033814 6 -48.91033814 6 -33.84765625 19.94921875 C-26.46373086 28.14490761 -22.57184671 37.46302465 -20 48 C-16.43987442 40.07924724 -12.97871812 32.15998087 -10 24 C-7.02128188 15.84001913 -3.56012558 7.92075276 0 0 Z M-105 43 C-105 54.22 -105 65.44 -105 77 C-99.98198272 77.09207371 -94.96692096 77.17163196 -89.94873047 77.21972656 C-88.24355106 77.23978369 -86.53843994 77.26703657 -84.83349609 77.30175781 C-82.37580476 77.35053861 -79.91902634 77.37302719 -77.4609375 77.390625 C-76.32620499 77.42159271 -76.32620499 77.42159271 -75.16854858 77.45318604 C-69.98503004 77.45536024 -67.1335432 76.38121828 -63 73 C-59.30220181 68.80249935 -58.17680443 64.99268158 -57.83984375 59.52734375 C-58.19670992 53.89582148 -60.85114313 49.7574107 -65 46.0625 C-69.80210776 42.76105092 -73.90043337 42.86014145 -79.609375 42.90234375 C-80.25406738 42.90335083 -80.89875977 42.90435791 -81.56298828 42.90539551 C-83.95867709 42.91060598 -86.35434043 42.92456719 -88.75 42.9375 C-94.1125 42.958125 -99.475 42.97875 -105 43 Z M-105 114 C-105 125.22 -105 136.44 -105 148 C-100.00028509 148.09216064 -95.00355174 148.17169227 -90.00366211 148.21972656 C-88.30531798 148.23976221 -86.60704215 148.26699885 -84.90893555 148.30175781 C-82.4586496 148.35063415 -80.0092806 148.37304872 -77.55859375 148.390625 C-76.80726669 148.41127014 -76.05593964 148.43191528 -75.28184509 148.45318604 C-69.74110807 148.45551032 -66.56146092 147.17474533 -62 144 C-58.29413591 139.22138577 -56.56605869 135.31314158 -56.65625 129.19140625 C-57.47903497 123.94615207 -60.53557588 120.18761224 -64.5625 116.921875 C-68.8302779 114.53579918 -71.88374644 114.39819535 -76.6796875 114.390625 C-77.49245148 114.37408875 -78.30521545 114.35755249 -79.14260864 114.34051514 C-81.72015063 114.29221045 -84.29710344 114.27070406 -86.875 114.25 C-88.6302377 114.22182552 -90.38544815 114.19189603 -92.140625 114.16015625 C-96.4273497 114.08317348 -100.71224206 114.04249512 -105 114 Z" fill="#00384D" transform="translate(163,35)"/>
        <path d="M0 0 C1.98 0 3.96 0 6 0 C12.89857705 16.17309907 19.4967025 32.46677238 26.09454346 48.76403809 C27.63354371 52.56449209 29.1742078 56.36427135 30.71484375 60.1640625 C31.02568909 60.93076263 31.33653442 61.69746277 31.65679932 62.48739624 C32.28781371 64.04378569 32.91883587 65.60017199 33.54986572 67.15655518 C34.81838993 70.2852882 36.08685127 73.41404671 37.35528564 76.54281616 C37.98413418 78.09395018 38.61299966 79.64507733 39.24188232 81.19619751 C40.74037702 84.89231114 42.23853912 88.58855876 43.73583984 92.28515625 C50.01700032 107.79160842 56.33690053 123.28052031 62.74707031 138.73413086 C66.20411009 147.06860285 69.63668807 155.41264355 73.03979492 163.76928711 C74.55813276 167.49444131 76.08221627 171.21536222 77.6640625 174.9140625 C78.35628906 176.53441406 78.35628906 176.53441406 79.0625 178.1875 C79.44535156 179.06792969 79.82820312 179.94835937 80.22265625 180.85546875 C81 183 81 183 81 186 C67.14 186 53.28 186 39 186 C33.48945616 172.77469478 33.48945616 172.77469478 31.3125 166.3125 C28.82300576 159.06530566 26.01003639 151.97289785 23.125 144.875 C18.68385376 133.91739775 14.7030233 122.84935222 10.90380859 111.65527344 C9.47939514 107.47052987 8.02504075 103.29654479 6.5625 99.125 C6.31532227 98.41085938 6.06814453 97.69671875 5.81347656 96.9609375 C4.85077482 93.72715746 4.85077482 93.72715746 3 91 C2.81731567 91.52157181 2.63463135 92.04314362 2.44641113 92.58052063 C0.50589126 98.11699031 -1.44306823 103.65044077 -3.39648438 109.18237305 C-4.11991166 111.23437773 -4.84133801 113.28708917 -5.56054688 115.34057617 C-9.50243662 126.59092769 -13.55542278 137.73780563 -18.30761719 148.67500305 C-21.00280561 154.87994204 -23.28616637 161.18474486 -25.48046875 167.58203125 C-27.59864912 173.74054539 -29.80957492 179.86680976 -32 186 C-45.53 186 -59.06 186 -73 186 C-72.11515931 181.57579654 -70.85287127 177.62194276 -69.25 173.4375 C-68.97978027 172.7249707 -68.70956055 172.01244141 -68.43115234 171.27832031 C-66.52062769 166.27429752 -64.50290593 161.32299077 -62.4140625 156.390625 C-60.56055698 151.9463079 -58.78136148 147.4736277 -57 143 C-54.45724342 136.61895353 -51.87527355 130.25885825 -49.21484375 123.92578125 C-47.75941774 120.42059127 -46.34611583 116.89916595 -44.9375 113.375 C-44.68081543 112.73860596 -44.42413086 112.10221191 -44.15966797 111.4465332 C-43.03428849 108.64687059 -41.94553816 105.84565796 -40.94140625 103 C-39.20631262 98.16189661 -37.89526081 95.47775582 -33.58203125 92.3984375 C-27.48342349 87.0075694 -23.21393172 76.93469915 -22 69 C-21.98168382 67.04220408 -21.96478911 65.08434633 -21.96411133 63.12646484 C-21.83555548 52.24993208 -18.58810245 44.00237802 -14.02246094 34.26220703 C-12.4773705 30.84367599 -11.20511471 27.34125349 -9.9375 23.8125 C-6.98289168 15.71574667 -3.53229664 7.8588375 0 0 Z" fill="#6AD8EC" transform="translate(163,35)"/>
        <path d="M0 0 C11.04090611 10.7063332 17.87317977 20.07962234 18.23486328 35.88183594 C18.34525221 51.32916448 14.25667984 63.38567492 3.34765625 74.75 C-3.62115505 81.4289273 -12.26001422 88.73925026 -22.33203125 89.12890625 C-23.21246094 89.08636719 -24.09289062 89.04382813 -25 89 C-25 89.66 -25 90.32 -25 91 C-28.63 91 -32.26 91 -36 91 C-35.11515931 86.57579654 -33.85287127 82.62194276 -32.25 78.4375 C-31.97978027 77.7249707 -31.70956055 77.01244141 -31.43115234 76.27832031 C-29.52062769 71.27429752 -27.50290593 66.32299077 -25.4140625 61.390625 C-23.56055698 56.9463079 -21.78136148 52.4736277 -20 48 C-17.45724342 41.61895353 -14.87527355 35.25885825 -12.21484375 28.92578125 C-10.75942471 25.42060805 -9.34482832 21.89967195 -7.9375 18.375 C-7.68444092 17.74150635 -7.43138184 17.1080127 -7.1706543 16.45532227 C-6.02879285 13.59266408 -4.89300382 10.72776921 -3.765625 7.859375 C-3.40984375 6.95703125 -3.0540625 6.0546875 -2.6875 5.125 C-2.38199219 4.34382812 -2.07648438 3.56265625 -1.76171875 2.7578125 C-1 1 -1 1 0 0 Z" fill="#5CB9CA" transform="translate(126,130)"/>
      </g>
    </svg>
  `;

    return {
      url: `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(svg)}`,
      scaledSize: new window.google.maps.Size(44, 58),
      anchor: new window.google.maps.Point(22, 58),
    };
  };

  return (
    <div className="relative w-full h-[2000px] md:h-[1350px] overflow-hidden">
      {/* MAPA GOOGLE */}
      <div className="absolute inset-0 z-0">
        <GoogleMap
          mapContainerStyle={containerStyle}
          center={mapCenter}
          zoom={selectedCity ? 11 : 7}
          onLoad={handleMapLoad}
          options={{
            heading: 40,
            disableDefaultUI: true,
            draggable: false,
            zoomControl: false,
            scrollwheel: false,
            disableDoubleClickZoom: true,
            gestureHandling: "none",
            mapTypeId: "roadmap",
            styles: [
              {
                featureType: "landscape",
                elementType: "geometry",
                stylers: [{ color: "#d5ead5" }],
              },
              {
                featureType: "poi.park",
                elementType: "geometry",
                stylers: [{ color: "#b5dcb5" }],
              },
              {
                featureType: "water",
                elementType: "geometry",
                stylers: [{ color: "#8fc8e3" }],
              },
              {
                featureType: "road",
                elementType: "geometry",
                stylers: [{ color: "#e8e8e8" }],
              },
              {
                featureType: "road",
                elementType: "labels.text.fill",
                stylers: [{ color: "#333333" }],
              },
              {
                featureType: "all",
                elementType: "labels.text.stroke",
                stylers: [{ color: "#ffffff" }],
              },
            ],
          }}
        >
          {!selectedCity &&
            CitiesFilter.map((city) => (
              <Marker
                key={city.id}
                position={{ lat: city.lat, lng: city.lng }}
                onClick={() => handleCityClick(city)}
                animation={window.google?.maps?.Animation?.DROP}
                icon={getCustomPinIcon()}
              />
            ))}

          {selectedCity && (
            <Marker
              position={{ lat: selectedCity.lat, lng: selectedCity.lng }}
              onClick={() => setSelectedCity(selectedCity)}
              animation={window.google?.maps?.Animation?.DROP}
              icon={getCustomPinIcon()}
            />
          )}

          {selectedCity && (
            <InfoWindow
              position={{
                lat: Number(selectedCity.lat),
                lng: Number(selectedCity.lng),
              }}
              onCloseClick={handleCloseSelectedCity}
            >
              <div className="text-sm text-gray-800">
                <hr />

                <p className="mt-3 text-base font-semibold">
                  {selectedCity.name}
                </p>

                <p className="mb-1 text-base font-semibold text-gray-600">
                  {selectedCity.phone}
                </p>

                {selectedCity.address && <p>{selectedCity.address}</p>}

                {selectedCity.neighborhood && (
                  <p>
                    {selectedCity.neighborhood} - {selectedCity.cidade} /{" "}
                    {selectedCity.state}
                  </p>
                )}

                {selectedCity.cep && <p>CEP: {selectedCity.cep}</p>}

                <p className="text-gray-500">{selectedCity.hours}</p>

                <a href={`/city/${selectedCity.id}`}>
                  <button
                    className={`mt-2 flex items-center rounded-lg px-3 py-1 font-medium shadow-md transition-colors ${theme.buttonBg} ${theme.buttonHoverBg}`}
                  >
                    <span className="font-medium text-white">Ver Mais..</span>
                  </button>
                </a>
              </div>
            </InfoWindow>
          )}
        </GoogleMap>
      </div>

      {/* CONTROLES E SOBREPOSIÇÕES */}
      <div className={`absolute inset-0 z-[5] pointer-events-none bg-gradient-to-b ${CityActive
        ? "from-[#becdd5] md:from-[#bcced7] lg:from-[#d3dadc]"
        : "from-[#f1f5f9]"} via-white/20 to-transparent h-[16%] md:h-[20%]`}></div>

      <div className="absolute inset-0 z-10 flex flex-col justify-between items-center pointer-events-none mt-4 md:mt-[30px]">
        {/* CARD DE AJUDA */}
        <div className="mx-4 mt-2 flex w-auto max-w-5xl flex-col gap-4 rounded-2xl bg-white/80 px-4 py-4 shadow-lg backdrop-blur-sm pointer-events-auto md:mt-4 md:w-full md:flex-row md:items-center md:justify-between md:px-10 md:py-6">
          <div className="md:pl-8">
            <p className="mb-1 text-[13px] font-semibold text-gray-900 md:text-[14px]">Cuide do que você tem de mais valioso.</p>
            <p className="text-[12px] text-gray-400 md:text-[13px]">Se o seu corpo e sua mente têm dado sinais, não ignore. Acompanharemos seu processo de recuperação do bem-estar, da energia e da melhor versão da sua performance física e mental.</p>
          </div>

          <button
            className={`inline-flex items-center justify-center self-start rounded-full bg-black px-5 py-3 text-sm font-semibold text-white shadow-[inset_0_0_10px_1px_rgba(255,255,255,0.8)] transition-colors hover:bg-gray-800 whitespace-nowrap pointer-events-auto md:mr-8 md:self-auto ${!contactCity ? "opacity-40 pointer-events-none" : ""
              }`}
            onClick={() => setOpen(true)}
            target="_blank"
            rel="noreferrer"
          >
            Entrar em contato
          </button>
        </div>

        {/* CARROSSEL DE CIDADES */}
        {!CityActive ? (
          <div className="mt-4 flex w-full flex-col items-center pointer-events-auto md:mt-6">
            <>
              <div className="w-full overflow-hidden">
                <div
                  className={`flex gap-4 ${citiesCount > 1 ? "animate-slide-Map" : ""
                    }`}
                  style={
                    citiesCount > 1 ? { animationDuration: `${duration}s` } : {}
                  }
                >
                  {citiesToRender.map((city, index) => (
                    <div
                      key={`${city.id}-${index}`}
                      onClick={() => handleCityClick(city)}
                      className={`group relative m-2 w-[280px] flex-shrink-0 rounded-2xl bg-white/65 p-1 shadow-lg backdrop-blur-md transition-all duration-300 cursor-pointer md:w-[460px] md:hover:scale-105 ${selectedCity?.id === city.id
                        ? "border-4"
                        : "border-4 border-transparent"
                        }`}
                    >
                      <div className="flex flex-row">
                        <div className="flex h-full w-[82%] flex-col justify-between md:w-[85%]">
                          <div className="mb-2 flex h-24 w-full items-center justify-center rounded-tl-lg rounded-tr-none bg-white md:h-28">
                            <img
                              src={city.image}
                              alt={`Foto de ${city.name}`}
                              className="w-full h-full object-cover rounded-l-xl"
                            />
                          </div>

                          <div className="mb-0 p-2">
                            <p className="mb-1 text-sm font-bold text-gray-800 md:text-base">
                              {(city.cpap &&
                                theme?.context?.includes("respirat")
                                ? "Equipamentos Respiratórios "
                                : city.aud &&
                                  theme?.context?.includes("audit")
                                  ? "Equipamentos Auditivos "
                                  : city.home &&
                                    (theme?.context?.includes("home") ||
                                      theme?.context === "")
                                    ? "Home Care "
                                    : "") + city.name}
                            </p>

                            <p className="mb-2 text-xs text-gray-600 md:text-sm">
                              {city.hours}
                            </p>

                            <div className="flex flex-wrap gap-2">
                              {city.cpap &&
                                (theme?.context?.includes("respirat") ||
                                  theme?.context === "") && (
                                  <span className="rounded-full bg-green-600 px-2 py-1 text-[10px] font-semibold text-white shadow md:px-3 md:text-xs">
                                    Equipamentos Respiratórios
                                  </span>
                                )}

                              {city.aud &&
                                (theme?.context?.includes("audit") ||
                                  theme?.context === "") && (
                                  <span className="rounded-full bg-purple-400 px-2 py-1 text-[10px] font-semibold text-white shadow md:px-3 md:text-xs">
                                    Aparelhos Auditivos
                                  </span>
                                )}

                              {city.home &&
                                (theme?.context?.includes("home") ||
                                  theme?.context === "") && (
                                  <span className="rounded-full bg-blue-400 px-2 py-1 text-[10px] font-semibold text-white shadow md:px-3 md:text-xs">
                                    Home Care
                                  </span>
                                )}
                            </div>
                          </div>
                        </div>

                        <div className="flex w-[18%] items-center md:w-[15%]">
                          <div className="flex h-[90%] items-center border-l border-gray-400">
                            <svg
                              width="25"
                              height="29"
                              viewBox="0 0 30 34"
                              fill="none"
                              xmlns="http://www.w3.org/2000/svg"
                            >
                              <path
                                d="M11.0242 5.35522L22.0889 16.6295L11.0242 27.9035"
                                stroke="#A7A7A7"
                                strokeWidth="2"
                                strokeMiterlimit="10"
                              />
                              <path
                                d="M7.33594 24.8353L7.33594 8.42334"
                                stroke="#A7A7A7"
                                strokeWidth="2"
                                strokeMiterlimit="10"
                              />
                            </svg>
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* PROGRESS BAR SINCRONIZADA */}
              {citiesCount > 1 && (
                <div className="mt-6 hidden h-6 w-[87vw] max-w-none items-center overflow-hidden rounded-full border border-white bg-white/80 border-4 md:flex">
                  <div
                    className="ml-2 h-[60%] rounded-full animate-progress-bar"
                    style={{
                      backgroundColor: theme.progressBar,
                      borderLeftColor: theme.progressBar,
                      borderLeftWidth: "6px",
                      animationDuration: `${duration}s`,
                    }}
                  ></div>
                </div>
              )}

              <div className="w-full !mt-0">
                <Footer />
              </div>
            </>
          </div>
        ) : (
          <div className="w-full !mt-0">
            <Footer />
          </div>
        )}
      </div>

      <div
        className="absolute inset-x-0 bottom-0 z-[5] pointer-events-none to-transparent h-[1600px]"
        style={{
          backgroundImage: `linear-gradient(to top, ${theme.gradientColor}, rgba(255, 255, 255, 0.3), transparent)`,
        }}
      ></div>
      <ContactModal isOpen={open} onClose={() => setOpen(false)} />
    </div>
  );
};

const MapLoader = ({ children }) => {
  const { isLoaded, loadError } = useLoadScript({
    googleMapsApiKey: API_KEY,
  });

  if (loadError) {
    return (
      <div className="text-red-600 text-center p-8">
        Erro ao carregar o mapa.
      </div>
    );
  }

  if (!isLoaded) {
    return (
      <div className="text-gray-500 text-center p-8">
        Carregando Mapa...
      </div>
    );
  }

  return <>{children}</>;
};

const MapAndCards = (props) => (
  <MapLoader>
    <MapContent {...props} />
  </MapLoader>
);

export default MapAndCards;
